import { useParams } from 'react-router-dom';
import i18n from 'i18next';
import { isTransportError } from '@/app/api/loadResilience';
import ThemeProvider from '@/features/restaurant/ThemeProvider';
import ConnectionLostNotice from '@/components/chrome/ConnectionLostNotice';
import GlobalLoader from '@/components/ui/GlobalLoader';
import NotFoundPage from '@/pages/NotFoundPage';
import SurveyPage from './SurveyPage';
import { useGetPublicSurveyQuery } from './surveyApi';
import { useDocumentLanguage } from './useDocumentLanguage';

function SurveyClosed({ storeName }: { storeName: string }) {
  const t = i18n.getFixedT('fr');
  useDocumentLanguage('fr', `${t('survey.title')} — ${storeName}`);
  return (
    <main className="min-h-[100dvh] bg-cream text-ink flex items-center justify-center p-8">
      <div className="text-center">
        <p className="font-mono text-sm text-ink-mute mb-2">{storeName}</p>
        <p className="font-display text-2xl">{t('survey.closed')}</p>
      </div>
    </main>
  );
}

/**
 * SC-1752 — own shell, sibling of `reset-password`, outside `RestaurantShell`:
 * a restaurant with online ordering off must still serve its survey, and no
 * announcement pop-up may sit on top of it. The theme comes from the survey
 * payload — `/restaurants/public/<uuid>/` 404s when online ordering is off.
 */
export default function SurveyShell() {
  const { restaurantUuid = '', surveyUuid = '' } = useParams<{
    restaurantUuid: string;
    surveyUuid: string;
  }>();
  const { data, isLoading, error, refetch } = useGetPublicSurveyQuery(
    { restaurantUuid, surveyUuid },
    { skip: !restaurantUuid || !surveyUuid, refetchOnMountOrArgChange: true },
  );

  if (isLoading) return <GlobalLoader />;
  if (isTransportError(error)) return <ConnectionLostNotice onRetry={refetch} />;
  if (!data) {
    const status = (error as { status?: unknown } | undefined)?.status;
    return status === 404 || !error ? <NotFoundPage /> : <ConnectionLostNotice onRetry={refetch} />;
  }

  const body =
    data.status === 'open' && data.schema && data.version !== null ? (
      <SurveyPage
        survey={{ ...data, schema: data.schema, version: data.version }}
        restaurantUuid={restaurantUuid}
      />
    ) : (
      <SurveyClosed storeName={data.restaurant.store_name} />
    );
  return data.restaurant.theme ? <ThemeProvider theme={data.restaurant.theme}>{body}</ThemeProvider> : body;
}
